// Chess VS intro — gold sparks, "MATCH FOUND" banner, 2 player cards
// + V/S divider + pot chip. Every class is prefixed `chess-vs-*` so
// the rules cannot leak into Ludo / Rummy / X&O (I've fixed this
// collision in this codebase twice already; every new VS keyframe
// stays scoped.)
//
// Shown for ~2.8 s after `chess_match_start`. The engine's
// `FIRST_MOVE_GRACE_SECONDS` config adds a server-side clock bonus
// to cover this visual window so white doesn't bleed time.

type SideProfile = {
  side: 'w' | 'b';
  displayName?: string;
  avatarUrl?: string | null;
  isBot?: boolean;
};

type Props = {
  mySide: 'w' | 'b';
  profiles: { w: SideProfile; b: SideProfile };
  pot: number;
  payout: number;
};

function fmt(n: number) {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

// Deterministic spark positions — a 2.8 s overlay doesn't justify a
// re-randomize on every render. Fixed seed gives a stable twinkle.
const SPARK_COUNT = 14;
const SPARKS = Array.from({ length: SPARK_COUNT }).map((_, i) => ({
  left:  `${(i * 7 + 5) % 95}%`,
  top:   `${85 + ((i * 13) % 10)}%`,
  delay: `${(i * 0.23) % 2.8}s`,
  scale: 0.6 + ((i * 0.17) % 0.8),
}));

export default function VsIntro({ mySide, profiles, pot, payout }: Props) {
  const me  = profiles[mySide];
  const opp = profiles[mySide === 'w' ? 'b' : 'w'];
  return (
    <div id="chess-vs-screen" className="chess-vs-overlay">
      <div className="chess-vs-sparks" aria-hidden>
        {SPARKS.map((s, i) => (
          <span
            key={i}
            className="chess-vs-spark"
            style={{
              left: s.left, top: s.top,
              animationDelay: s.delay,
              transform: `scale(${s.scale})`,
            }}
          />
        ))}
      </div>

      <div className="chess-vs-banner">Match Found</div>

      <div className="chess-vs-stage">
        <VsCard profile={me} elevated />
        <div className="chess-vs-divider"><span>V/S</span></div>
        <VsCard profile={opp} />
      </div>

      <div className="chess-vs-pot">POT {fmt(pot)} &middot; WIN {fmt(payout)}</div>
    </div>
  );
}

function VsCard({ profile, elevated }: { profile: SideProfile; elevated?: boolean }) {
  const name = profile.displayName || (profile.side === 'w' ? 'White' : 'Black');
  return (
    <div className={`chess-vs-card ${elevated ? 'chess-vs-card--me' : ''}`}>
      {elevated && <div className="chess-vs-you-badge">YOU</div>}
      <div className="chess-vs-card-ring">
        {profile.avatarUrl
          ? <img src={profile.avatarUrl} alt="" />
          : (
            <div style={{
              width: '100%', height: '100%',
              background: '#3a2515',
              display: 'grid', placeItems: 'center',
              color: '#f6d07c', fontWeight: 900, fontSize: 26,
            }}>
              {name.slice(0, 1).toUpperCase()}
            </div>
          )}
      </div>
      <div className="chess-vs-card-name">{name}</div>
      <span className="chess-vs-card-side">
        {/* Side label only. " · BOT" suffix removed — players must
            never see that they're playing against a bot. */}
        {profile.side === 'w' ? 'WHITE' : 'BLACK'}
      </span>
    </div>
  );
}
